import Vue from 'vue';
import styled, { css } from 'vue-styled-components';

import RingLoader from '@components/Common/RingLoader';

import DescendingIcon from '@svgIcons/dashboard/descending.svg';

import {
    FLEX_VERTICAL_CENTER_DIV,
    GRAY_100_DIV,
    FLEX_DIV,
} from '@styles/common.styled.component';
import {
    applyHoverAndPress,
    square,
    COLORS,
    emptyCss,
} from '@styles/common.styled';

import VirtualList from 'vue-virtual-scroll-list';

import { isValueExist } from '@util';

import { partition, switchMap, takeUntil, filter } from 'rxjs/operators';
import { combineLatest, fromEvent } from 'rxjs';

const IDENTITY = R.identity;

export default {
    name: 'Dropdown',
    props: {
        value: {
            type: undefined,
        },
        options: {
            type: Array,
            default() {
                return [];
            },
        },
        trigger: {
            type: Array,
            default() {
                return ['click'];
            },
        },
        formatter: {
            type: Function,
            default: IDENTITY,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        borderLess: {
            type: Boolean,
            default: false,
        },
        dropdownTrigger: Object,
        customStyles: {
            type: Object,
            default() {
                return {};
            },
        },
        itemWrapperStyle: {
            type: Object,
            default() {
                return {};
            },
        },
        itemStyle: {
            type: Object,
            default() {
                return {};
            },
        },
        placement: {
            type: String,
            default: 'bottomLeft',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: '',
        },
        idExtractor: {
            type: Function,
            default: R.prop('id'),
        },
        customDesIcon: {
            type: Object,
            default: null,
        },
        arrowIconVisible: {
            type: Boolean,
            default: true,
        },
        visible: Boolean,
        hideOptionAfterInput: {
            type: Boolean,
            default: true,
        },
        disableHover: {
            type: Boolean,
            default: false,
        },
        getPopupContainer: {
            type: Function,
        },
        hideOnScroll: {
            type: Boolean,
            default: false,
        },
        scrollSelector: undefined,
    },
    mounted() {
        this.handleScroll$();
    },
    data() {
        return {
            showOptions: false,
        };
    },
    watch: {
        visible(v) {
            if (v === false) {
                this.showOptions = false;
            }
        },
    },
    computed: {
        _options() {
            const { showOptions, options, visible } = this;

            return showOptions || visible ? options : [];
        },
        elemHeight: R.compose(
            parseInt,
            R.pathOr(54, ['customStyles', 'menuItemHeight'])
        ),
        useVirtualScroll() {
            const { _options, elemHeight, idExtractor } = this;

            const isValid = R.all(
                R.allPass([R.compose(isValueExist, idExtractor), R.is(Object)]),
                _options
            );

            return isValid && _options.length * elemHeight > 1000;
        },
    },
    render() {
        const {
            _options = [],
            value,
            trigger,
            disabled,
            isLoading,
            borderLess,
            dropdownTrigger,
            placement,
            placeholder,
            renderOptions,
            arrowIconVisible,
            visible,
            disableHover,
            getPopupContainer,
            itemWrapperStyle,
        } = this;

        const hasPlaceholder = !!(!value && placeholder);

        return (
            <a-dropdown
                trigger={trigger}
                disabled={disabled || isLoading}
                placement={placement}
                visible={this.showOptions || visible}
                getPopupContainer={getPopupContainer}
                on={{
                    visibleChange: (v) => {
                        this.showOptions = v;
                        this.$emit('visibleChange', v);
                    },
                }}
            >
                {dropdownTrigger ? (
                    dropdownTrigger
                ) : (
                    <DropdownTrigger
                        class="dropdown-trigger"
                        borderLess={borderLess}
                        disabled={disabled}
                        disableHover={disableHover}
                    >
                        {isLoading ? (
                            <LoaderWrapper>
                                <RingLoader size={20} />
                            </LoaderWrapper>
                        ) : (
                            [
                                <DropdownText
                                    hasPlaceholder={hasPlaceholder}
                                    class="dropdown-text"
                                >
                                    {value || placeholder}
                                </DropdownText>,
                                !disabled &&
                                    arrowIconVisible &&
                                    R.or(
                                        this.customDesIcon,
                                        <DescendingIcon
                                            v-view-box
                                            class="arrow-icon"
                                        />
                                    ),
                            ]
                        )}
                    </DropdownTrigger>
                )}
                {_options.length && (
                    <a-menu
                        slot="overlay"
                        style={{
                            ...itemWrapperStyle,
                        }}
                        onClick={() => {
                            this.$emit('itemClick');
                        }}
                    >
                        {renderOptions(_options)}
                    </a-menu>
                )}
            </a-dropdown>
        );
    },
    methods: {
        renderOptions(options) {
            const { formatter, useVirtualScroll, customStyles, itemStyle } =
                this;

            const onInput = (value, index, event) => {
                this.$emit('input', {
                    value,
                    index,
                    event,
                });
                if (this.hideOptionAfterInput) {
                    this.showOptions = false;
                }
            };

            const onMousedown = (e) => this.$emit('mousedown', e);

            if (useVirtualScroll) {
                return (
                    <VirtualList
                        class="scroller"
                        style="height: 480px; overflow-y: auto;"
                        data-key={this.idExtractor}
                        data-sources={options}
                        data-component={OptionElem}
                        keeps={20}
                        extra-props={{
                            formatter,
                            inputFunc: onInput,
                            mousedownFunc: onMousedown,
                            customStyles,
                        }}
                    />
                );
            } else {
                return (
                    <MenuWrapper
                        class="dropdown-menu-wrapper"
                        customStyles={customStyles}
                    >
                        {options.map((value, index) => {
                            return (
                                <OptionElem
                                    source={value}
                                    index={index}
                                    formatter={formatter}
                                    inputFunc={onInput}
                                    mousedownFunc={onMousedown}
                                    customStyles={customStyles}
                                    itemStyle={itemStyle}
                                />
                            );
                        })}
                    </MenuWrapper>
                );
            }
        },
        handleScroll$() {
            const { generateWatchNew$: gen$ } = this;

            const on$ = gen$('hideOnScroll').pipe(filter(R.identity));

            const [show$, hide$] = gen$('showOptions').pipe(
                partition(R.identity)
            );

            const obs$ = combineLatest(on$, show$).pipe(
                filter(R.all(R.equals(true))),
                switchMap(() => {
                    const selector = this.scrollSelector;

                    const dom$ = selector
                        ? document.querySelector(selector)
                        : document;

                    return fromEvent(dom$ || document, 'scroll').pipe(
                        takeUntil(hide$.pipe(filter(R.equals(false))))
                    );
                })
            );

            this.$subscribeTo(obs$, () => (this.showOptions = false));
        },
    },
};

const OptionElem = {
    name: 'OptionElem',
    props: {
        source: {
            default() {
                return {};
            },
        },
        index: {
            type: Number,
            default: IDENTITY,
        },
        formatter: {
            type: Function,
            default: IDENTITY,
        },
        inputFunc: {
            type: Function,
            default: IDENTITY,
        },
        mousedownFunc: {
            type: Function,
            default: IDENTITY,
        },
        customStyles: {
            type: Object,
            default() {
                return {};
            },
        },
        itemStyle: {
            type: Object,
            default() {
                return {};
            },
        },
    },
    render() {
        const {
            source,
            index,
            formatter,
            inputFunc,
            mousedownFunc,
            customStyles,
            itemStyle,
        } = this;

        const value = source;

        if (!value) {
            return null;
        }

        const _css = value.css || emptyCss;

        const _DropdownOptionText = styled(DropdownOptionText)`
            ${_css}
        `;

        const Elem = (
            <_DropdownOptionText
                class="dropdown-option-text"
                customStyles={customStyles}
                nativeOnMousedown={(event) => {
                    mousedownFunc({
                        event,
                        value,
                        index,
                    });
                }}
            >
                {value.icon}
                {formatter(value)}
            </_DropdownOptionText>
        );

        const classes = ['dropdown-menu-item', value.className].filter(
            IDENTITY
        );

        return (
            <MenuItem
                class={classes}
                customStyles={customStyles}
                itemStyle={itemStyle}
                onClick={({ domEvent: event } = {}) =>
                    inputFunc(value, index, event)
                }
                disabled={value.disabled}
            >
                {Elem}
            </MenuItem>
        );
    },
};

export const DropdownTrigger = styled(FLEX_VERTICAL_CENTER_DIV, {
    borderLess: Boolean,
    disabled: Boolean,
    disableHover: Boolean,
})`
    height: 36px;
    border-radius: 2px;
    width: auto;
    ${({ disabled, disableHover }) =>
        disabled
            ? css`
                  cursor: not-allowed;
                  & > * {
                      //   padding: 0 !important;
                  }
                  background-color: ${COLORS['gray-15']} !important;
                  div {
                      color: ${COLORS['gray-40']} !important;
                  }
              `
            : css`
                  ${!disableHover &&
                  applyHoverAndPress('white', 'background-color')};
                  cursor: pointer;
              `};
    ${({ borderLess }) => {
        return !borderLess
            ? css`
                  border: solid 1px ${COLORS['gray-30']};
                  min-width: 96px;
                  svg {
                      margin-left: auto;
                  }
              `
            : css`
                  svg {
                      margin-left: 8px;
                  }
              `;
    }}
    svg {
        ${square(16)};
        margin-right: 8px;
        fill: ${COLORS['gray-80']};
        flex-shrink: 0;
    }
`;
export const MenuItem = styled(Vue.options.components.AMenuItem, {
    customStyles: Object,
    disabled: Boolean,
    disableHover: Boolean,
    itemStyle: Object,
})`
    display: flex;
    align-items: center;
    height: ${R.pathOr('54px', ['customStyles', 'menuItemHeight'])};
    ${applyHoverAndPress('white', 'background-color')};
    ${({ customStyles: { menuItemMinWidth } = {} }) =>
        menuItemMinWidth &&
        css`
            min-width: ${menuItemMinWidth};
        `};
    @media screen and (max-width: 767px) {
        height: 48px;
    }

    ${R.path(['customStyles', 'menuItem'])};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background: ${({ disabled }) => (disabled ? COLORS['gray-30'] : undefined)};

    &:hover {
        background: ${({ disableHover }) =>
            disableHover ? COLORS['gray-35'] : undefined};
    }

    ${({ itemStyle = null }) =>
        itemStyle &&
        css`
            ${{ ...itemStyle }}
        `}
`;

export const DropdownText = styled('div', {
    hasPlaceholder: Boolean,
    disabled: Boolean,
})`
    font-size: 14px;
    font-weight: 400;
    padding-left: 8px;
    color: ${({ hasPlaceholder }) =>
        hasPlaceholder ? 'var(--color-gray-40)' : 'var(--color-gray-130)'};
    background-color: transparent;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DropdownOptionText = styled(DropdownText, { customStyles: Object })`
    width: 100%;
    padding: ${R.pathOr('0 12px', ['customStyles', 'menuItemPadding'])};
    display: flex;
    align-items: center;
`;

const MenuWrapper = styled('div', { customStyles: Object })`
    max-height: 400px;
    overflow-y: auto;
    ${({ customStyles: { menuWrapperCss = null } }) =>
        menuWrapperCss &&
        css`
            ${{ ...menuWrapperCss }}
        `}
`;

const LoaderWrapper = styled(FLEX_DIV)`
    width: 100%;
    justify-content: center;
`;

export const CUSTOM_STYLE_36 = {
    menuItemHeight: '36px',
};
